import { useState } from "react"
import {
  HeadingLarge,
  LabelLarge,
  Link,
  ParagraphLarge,
  useStyletron,
} from "@mezo-org/mezo-clay"

import links from "#/shared/constants/links"
import {
  useMarketGiftCardDataById,
  useMarketStoreProductAvailability,
  useMarketStoreProductDetailsByIds,
  useOrderProduct,
} from "#/shared/hooks"
import { bigIntToFloat } from "#/shared/utils"
import {
  MarketItemAmountPicker,
  MarketItemDetailsLayoutWithActionButton,
} from "#/ui/Market/MarketItemDetails/components"
import MarketRedeemCodeSuccess from "#/ui/Market/MarketItemDetails/MarketRedeemCodeSuccess"

import MarketTag from "../MarketTag"

export default function MarketGiftCardDetails({
  giftCardId,
}: {
  giftCardId: string
}) {
  const [, theme] = useStyletron()
  const giftCardData = useMarketGiftCardDataById(giftCardId)

  const { data: giftCardDataDetails } = useMarketStoreProductDetailsByIds(
    giftCardData?.chainProductId,
  )

  const [selectedGiftCardId, setSelectedGiftCardId] = useState<number>()

  const {
    isFetching: isProductAvailabilityDataFetching,
    isInStock,
    isLimitPerCustomerReached,
    submitButtonText,
  } = useMarketStoreProductAvailability(selectedGiftCardId)

  const {
    orderProduct,
    isProcessing,
    isSuccess,
    hasEnoughFunds,
    hasError,
    errorMessage,
    result: { transactionHash, redeemCode },
  } = useOrderProduct(selectedGiftCardId)

  if (!giftCardData) return null

  if (isSuccess && transactionHash && redeemCode) {
    return (
      <MarketRedeemCodeSuccess
        type="gift-card"
        transactionHash={transactionHash}
        redeemCode={redeemCode}
        image={giftCardData.fullImage}
        successMessage={giftCardData.successMessage}
        redeemButtonLabel="Redeem at bitrefill.com/redeem"
        redeemLink={`${links.BITREFILL}/redeem#${redeemCode}`}
      />
    )
  }

  return (
    <MarketItemDetailsLayoutWithActionButton
      itemData={giftCardData}
      submitButtonText={submitButtonText}
      submitCallback={orderProduct}
      submitButtonDataAttr={`${giftCardData.id}-purchase`}
      isSubmitButtonDisabled={
        !selectedGiftCardId ||
        !isInStock ||
        isLimitPerCustomerReached ||
        isProductAvailabilityDataFetching ||
        !hasEnoughFunds
      }
      isProcessing={isProcessing}
      hasError={hasError}
      errorMessage={errorMessage}
      termsOfUseNote={(link) => (
        <>By purchasing, you accept Mezo&apos;s {link}</>
      )}
    >
      <MarketTag
        overrides={{
          Root: {
            style: {
              marginBottom: theme.sizing.scale700,
            },
          },
        }}
      >
        Earn 2x bonus mats
      </MarketTag>

      <HeadingLarge marginBottom={theme.sizing.scale800}>
        {giftCardData.mainTitle}
      </HeadingLarge>
      {giftCardData.mainDescription && (
        <ParagraphLarge marginBottom={theme.sizing.scale600}>
          {giftCardData.mainDescription}
        </ParagraphLarge>
      )}
      <ParagraphLarge marginBottom={theme.sizing.scale600}>
        Use your MUSD to unlock digital gift cards through{" "}
        <Link href={links.BITREFILL} target="_blank">
          Bitrefill.com
        </Link>
        , a global platform that lets you live on crypto by shopping with brands
        like Amazon, Apple, Uber, and more.
      </ParagraphLarge>
      <LabelLarge marginBottom={theme.sizing.scale600}>
        Redemption Details
      </LabelLarge>
      <ParagraphLarge marginBottom={theme.sizing.scale600}>
        You’ll get your Bitrefill code instantly after purchase. To redeem,
        you’ll need to create a Bitrefill account using your email. Purchase
        limits may apply based on account tier and region, so be sure to review
        Bitrefill’s{" "}
        <Link href={links.BITREFILL_PURCHASE_LIMIT_INFO} target="_blank">
          purchase limit information
        </Link>{" "}
        and{" "}
        <Link href={links.BITREFILL_TERMS} target="_blank">
          Terms
        </Link>{" "}
        before purchasing.
      </ParagraphLarge>
      <LabelLarge marginBottom={theme.sizing.scale600}>
        Choose an amount
      </LabelLarge>
      <MarketItemAmountPicker
        amounts={
          giftCardDataDetails
            ? giftCardDataDetails.map((giftCardDataDetail) =>
                Number(bigIntToFloat(BigInt(giftCardDataDetail.price))),
              )
            : []
        }
        onPickAmountCallback={(value) => {
          const giftCardChainItem = giftCardDataDetails
            ? giftCardDataDetails.find(
                (giftCardDataDetail) =>
                  Number(bigIntToFloat(BigInt(giftCardDataDetail.price))) ===
                  value,
              )
            : undefined

          setSelectedGiftCardId(giftCardChainItem?.id)
        }}
        keepSelected
      />
    </MarketItemDetailsLayoutWithActionButton>
  )
}
